import React, { useEffect, useState } from 'react';

import {
  CONTACT,
  CONTACT_POST_PARSE,
  EDUCATION,
  RESUME_PROFILE_START,
  WORK_EXPERIENCE,
} from '@zr/jobseeker-public/apps/jobseeker-screens/labels';

import Contact from './Contact';
import ContactPostParse from './ContactPostParse';
import ResumeProfileStart from './ResumeProfileStart';
import WorkExperience from './WorkExperience';
import Education from './Education';

/**
 * Custom hook that returns the current Screen Component to render
 * This hook handles all wizard logic and telemetry
 *
 * @param {autologin} autologin
 * @param {Object} initialContact - Data used to pre-fill fields in the wizard
 * @param {string} initialContact.name - Jobseeker name, if known
 * @param {string} initialContact.phone_number - Jobseeker phone number, if known
 * @param {object} isMobile
 * @param {isOpen} isOpen - Prevents rendering a view before the modal is open
 * @param {method} method - location of invocation for event logging purposes
 * @param {function} onComplete - runs when wizard is done
 * @param {function} onError - runs when an error is encountered
 * @param {boolean} resumeRequired
 * @param {function} setIsLoading - method for setting loading state
 * @param {boolean} skipEducation
 * @param {boolean} skipExperience
 * @param {object} strings - supplies overrides for strings inside components
 * @param {object} telemetry - methods for logging wizard interactions
 * @returns Screen Component
 */
export default function useUploadResumeOrCreateProfileWizard({
  autologin,
  initialContact,
  isCheckIn,
  isMobile,
  isOpen = true,
  method,
  onComplete = () => {},
  onError = () => {},
  resumeRequired = false,
  setIsLoading = () => {},
  skipEducation = false,
  skipExperience = false,
  strings,
  telemetry,
}) {
  const {
    addViewProperties,
    logView,
    addInteractionProperties,
    logInteraction,
  } = telemetry;

  // the current screen view key to render
  const [currentScreen, setCurrentScreen] = useState(RESUME_PROFILE_START);
  // if they're finishing this wizard, disable the last screen after their last action (unless something goes wrong)
  const [disableScreen, setDisableScreen] = useState(false);

  // log the first screen view when the wizard begins
  useEffect(() => {
    if (isOpen) {
      addViewProperties({
        screen_view_name: RESUME_PROFILE_START,
      });
      logView();
    }
  // the log functions will update, and we don't want to log each time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // logAndContinueToScreen does
  // 1. advances to the next screen specified in the arg
  // 2. logs an interaction for the previous screen
  // 3. logs a view for the next screen
  const logAndContinueToScreen = (nextScreen) => {
    addInteractionProperties({
      // log name of screen they just finished
      screen_view_name: currentScreen,
      // if we're continuing to a new screen view, job seeker didn't finish applying
      finished_screen_type: false,
    });
    // log the interaction with all the properties we've added to it
    logInteraction();

    // This wizard controls the many different screens all under the `UploadResumeOrCreateProfile` screenType.
    // We need to `setIsLoading(false)` here because when one of the screens (i.e. `ResumeProfileStart`) disables
    // the close button during its processing/loading phase, we need to re-enable the button here so that close
    // button is available again when we advance to the next screen
    setIsLoading(false);

    setCurrentScreen(nextScreen);

    // log screen view of the next screen we're on
    addViewProperties({
      screen_view_name: nextScreen,
    });
    logView();
  };

  // logAddAnother logs when the job seeker adds another entry
  const logAddAnother = () => {
    addInteractionProperties({
      interaction: 'add_another',
      // get current screen so analytics can know which entry type they added
      screen_view_name: currentScreen,
    });
    logInteraction();
  };

  // when done with screen views
  // 1. disable screen to prevent any further action
  // 2. log the finishing interaction
  // 3. tell parent container we're done with this wizard/screen type
  const logAndFinish = async (options = {}) => {
    // chances are the apply is good, disable the screen to prevent any further action (double submits, etc.)
    setDisableScreen(true);
    try {
      // finished apply, log last interaction
      addInteractionProperties({
        // log name of screen they just finished
        screen_view_name: currentScreen,
        finished_screen_type: true,
      });
      logInteraction();
      await onComplete(options);
    } catch (e) {
      // if this gets here, the screen view remains and they can try submitting again
      onError(e);
      setDisableScreen(false);
    }
  };

  /**
   * When going through a flow where we parse the job seeker's resume, we need to relay the parse results to the post parse screen view
   * These properties are sent from useUploadAndParseResume.js (jsdocs copied from there)
   *
   * @param {string} option.errorMessage message to display on the next screen if something went wrong
   * @param {string} option.fileName file name job seeker picked, used to show their uploaded file on the next screen
   * @param {string} option.parseResult used in logging to indicate how the parse went
   * @param {string} option.parseContent used in logging to indicate what fields (name, phone_number) were successfully parsed
   * @param {string} option.resume raw resume data if next screen needs to upload the resume again
   * @param {object} option.resumeJso parsed resume data jso
   * @param {boolean} option.uploadedResume true if resume was uploaded
   */
  const [parseFirstResult, setParseFirstResult] = useState({
    errorMessage: null,
    fileName: null,
    parseResult: null,
    parseContent: null,
    resumeJso: null,
    uploadedResume: false,
  });

  /**
   * all functions below
   * 1. handle wizard logic on where to go after completing screen views
   * 2. gathers telemetry properties when we log screen_view and screen_interaction
   */

  const continueToEducation = () => {
    if (skipEducation) {
      logAndFinish();
    } else {
      logAndContinueToScreen(EDUCATION);
    }
  };

  const continueToProfileBuilder = () => {
    if (skipExperience) {
      continueToEducation();
    } else {
      logAndContinueToScreen(WORK_EXPERIENCE);
    }
  };

  const handleContactContinue = () => {
    addInteractionProperties({
      interaction: 'submit',
    });
    continueToProfileBuilder();
  };

  const handleNoParseResumeContinue = () => {
    addInteractionProperties({
      interaction: 'submit',
      resume_submitted: false,
    });
    logAndContinueToScreen(CONTACT);
  };

  // job seeker just submitted a resume with some parse result
  // go to post parse screen and add the parse result as a prop to it
  const handleParseFirstContinue = (result) => {
    // set the parse result to state so we can pass that to the screen component
    setParseFirstResult(result);
    // note in post parse screen view logging how the parse went
    addViewProperties({
      parse_content: result.parseContent,
      parse_result: result.parseResult,
    });
    // note in interaction logging that they went the resume path
    addInteractionProperties({
      interaction: 'submit',
      resume_submitted: true,
    });
    // CONTACT_POST_PARSE screen will use the parse result
    logAndContinueToScreen(CONTACT_POST_PARSE);
  };

  // job seeker parsed their resume, but they removed the resume they just uploaded with on post parse screen
  const handlePostParseNoResumeContinue = () => {
    // note they went through this by removing their parse first resume
    addInteractionProperties({
      interaction: 'submit',
      resume_submitted: false,
    });
    // they've changed their mind about their resume and submitted their contact info, take them to profile builder
    continueToProfileBuilder();
  };

  // they finished the post parse by going ahead with the parsed resume or uploaded a new resume
  const handlePostParseResumeFinish = () => {
    addInteractionProperties({
      interaction: 'submit',
      resume_submitted: true,
    });
    logAndFinish();
  };

  const handleWorkExperienceContinue = () => {
    addInteractionProperties({
      interaction: 'submit',
    });
    continueToEducation();
  };

  const handleEducationFinish = () => {
    addInteractionProperties({
      interaction: 'submit',
    });
    logAndFinish();
  };

  const continueToContactBuildProfile = () => {
    addInteractionProperties({
      interaction: 'will_build_profile',
    });
    logAndContinueToScreen(CONTACT);
  };

  const continueToContactSkippedResume = () => {
    addInteractionProperties({
      interaction: 'skip',
    });
    logAndFinish({ skip_upload_resume_or_create_profile: 1 });
  };

  // log if job seeker changed the parsed name we gave them
  const handleParsedNameChange = () => {
    addInteractionProperties({
      field_type: 'name',
      interaction: 'edit_parsed_contact_details',
      screen_view_name: currentScreen,
    });
    logInteraction();
  };

  // log if job seeker changed the parsed phone number we gave them
  const handleParsedPhoneNumberChange = () => {
    addInteractionProperties({
      field_type: 'name',
      interaction: 'edit_parsed_contact_details',
      screen_view_name: currentScreen,
    });
    logInteraction();
  };

  // these props are used by majority of screens
  const commonScreenProps = {
    // these group of users can't read data like parsed resume data
    autologin,
    // string used for profile_method and resume_method analytics
    method,
    // if they're finishing this wizard, disable the last screen after their last action (unless something goes wrong)
    disabled: disableScreen,
    setIsLoading,
  };

  /**
   * map of all screen view components
   * key: the screen view key/identifier set in currentScreen and defined above
   * value: screen view component to render
   * these components generally invoke the handler functions above when they're done
   */
  const screenViews = {
    [RESUME_PROFILE_START]: (
      <ResumeProfileStart
        {...commonScreenProps}
        isCheckIn={isCheckIn}
        isMobile={isMobile}
        onBuildProfileContinue={continueToContactBuildProfile}
        onNoResumeContinue={handleNoParseResumeContinue}
        onResumeContinueWithResult={handleParseFirstContinue}
        onSkip={continueToContactSkippedResume}
        resumeRequired={resumeRequired}
        strings={strings?.resumeProfileStart}
      />
    ),
    [CONTACT]: (
      <Contact
        {...commonScreenProps}
        onContinue={handleContactContinue}
        initialContact={initialContact}
        strings={strings?.contact}
      />
    ),
    [CONTACT_POST_PARSE]: (
      <ContactPostParse
        {...commonScreenProps}
        onNoResumeContinue={handlePostParseNoResumeContinue}
        onResumeContinue={handlePostParseResumeFinish}
        onParsedNameChange={handleParsedNameChange}
        onParsedPhoneNumberChange={handleParsedPhoneNumberChange}
        parseFirstResult={parseFirstResult}
        initialContact={initialContact}
        strings={strings?.contactPostParse}
      />
    ),
    [WORK_EXPERIENCE]: (
      <WorkExperience
        {...commonScreenProps}
        onAddAnother={logAddAnother}
        onContinue={handleWorkExperienceContinue}
        enableRichText
        strings={strings?.workExperience}
      />
    ),
    [EDUCATION]: (
      <Education
        {...commonScreenProps}
        onAddAnother={logAddAnother}
        onContinue={handleEducationFinish}
        strings={strings?.education}
      />
    ),
  };

  const Screen = screenViews[currentScreen];

  return Screen;
}
